import { computed, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import rolePermissionService, { type RolePermission } from "../services/role_permission";

export function useRolePermission() {
  const { t } = useI18n();
  const roles = ref<RolePermission[]>([]);
  const searchQuery = ref("");
  const loading = ref(false);
  const pageSizeOptions = ref(["10", "25", "50", "100", "1000"]);

  const fetchRoles = async () => {
    loading.value = true;
    try {
      const response = await rolePermissionService.getRoles();
      if (Array.isArray(response)) {
        roles.value = response;
      } else if (response?.data && Array.isArray(response.data)) {
        roles.value = response.data;
      } else {
        roles.value = [];
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      roles.value = [];
      Swal.fire({
        icon: "error",
        title: t("group_permission_page.load_failed"),
        text: t("group_permission_page.unable_to_load_roles"),
      });
    } finally {
      loading.value = false;
    }
  };

  const filteredRoles = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return roles.value;

    return roles.value.filter((role) =>
      [role.role_name, role.description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  });

  const paginationConfig = computed(() => ({
    showSizeChanger: true,
    pageSizeOptions: pageSizeOptions.value,
    showTotal: (total: number, range: [number, number]) =>
      t("group_permission_page.pagination_total", {
        start: range[0],
        end: range[1],
        total,
      }),
    defaultPageSize: 10,
    locale: { items_per_page: t("group_permission_page.items_per_page") },
  }));

  watch(
    filteredRoles,
    (newValue) => {
      const totalCount = newValue.length > 0 ? newValue.length : 1000;
      pageSizeOptions.value = ["10", "25", "50", "100", String(totalCount)];
    },
    { immediate: true },
  );

  const deleteRole = async (role: RolePermission) => {
    if (!role.id) return;

    const result = await Swal.fire({
      title: t("group_permission_page.delete_title"),
      text: t("group_permission_page.delete_text", { role: role.role_name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("group_permission_page.delete_confirm"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: t("group_permission_page.deleting_title"),
        text: t("group_permission_page.deleting_text"),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await rolePermissionService.deleteRole(role.id);
      await fetchRoles();

      Swal.fire({
        icon: "success",
        title: t("group_permission_page.deleted_title"),
        text: t("group_permission_page.deleted_text", { role: role.role_name }),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error deleting role:", error);
      Swal.fire({
        icon: "error",
        title: t("group_permission_page.delete_failed"),
        text: t("group_permission_page.unable_to_delete_role"),
      });
    }
  };

  return {
    roles,
    searchQuery,
    loading,
    filteredRoles,
    paginationConfig,
    fetchRoles,
    deleteRole,
  };
}
